import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { currencyToInteger } from '@actual-app/core/shared/util';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import { color, movimiento, radio } from '#components/mobile/ui/tokens';
import { useCategory } from '#hooks/useCategory';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNotes } from '#hooks/useNotes';
import { addNotification } from '#notifications/notificationsSlice';
import type { Notification } from '#notifications/notificationsSlice';
import { useDispatch, useStore } from '#redux';

import { notaConObjetivo, proximoVencimiento } from './objetivos';
import type { CadenciaMeses, Objetivo, TipoObjetivo } from './objetivos';
import { recuerdosDesdePrefs } from './recuerdos';
import { lineasConFecha, navegandoDe } from './travesias';
import { useAccionesRecuerdo } from './useRecuerdos';

const ALTO_BOTON = 44;

/** Formulario: igual que `Objetivo` pero con todos los campos a la vez. */
export type Formulario = {
  tipo: TipoObjetivo;
  importeTexto: string;
  dia: number | null;
  modo: 'apartar' | 'rellenar';
  fecha: string;
  cadaMeses: CadenciaMeses;
};

export function formularioDesde(
  objetivo: Objetivo | null,
  month: string,
  forEdit: (value: IntegerAmount) => string,
): Formulario {
  const base: Formulario = {
    tipo: 'mensual',
    importeTexto: '',
    dia: null,
    modo: 'apartar',
    fecha: `${monthUtils.addMonths(month, 12)}-01`,
    cadaMeses: 12,
  };
  if (!objetivo) {
    return base;
  }
  const importeTexto = forEdit(objetivo.importe);
  switch (objetivo.tipo) {
    case 'mensual':
      return {
        ...base,
        tipo: 'mensual',
        importeTexto,
        dia: objetivo.dia,
        modo: objetivo.modo,
      };
    case 'semanal':
      return { ...base, tipo: 'semanal', importeTexto, modo: objetivo.modo };
    case 'anual':
      return {
        ...base,
        tipo: 'anual',
        importeTexto,
        fecha: objetivo.fecha,
        cadaMeses: objetivo.cadaMeses,
      };
    case 'una-vez':
      return { ...base, tipo: 'una-vez', importeTexto, fecha: objetivo.fecha };
    default:
      return base;
  }
}

export function objetivoDesde(f: Formulario): Objetivo | null {
  const importe = currencyToInteger(f.importeTexto);
  if (importe == null || importe <= 0) {
    return null;
  }
  switch (f.tipo) {
    case 'mensual':
      return { tipo: 'mensual', importe, dia: f.dia, modo: f.modo };
    case 'semanal':
      return { tipo: 'semanal', importe, modo: f.modo };
    case 'anual':
      if (!monthUtils.isValidYearMonthDay(f.fecha)) {
        return null;
      }
      return { tipo: 'anual', importe, fecha: f.fecha, cadaMeses: f.cadaMeses };
    case 'una-vez':
      if (!monthUtils.isValidYearMonthDay(f.fecha)) {
        return null;
      }
      return { tipo: 'una-vez', importe, fecha: f.fecha };
    default:
      return null;
  }
}

/** Texto corto del objetivo («50,52 € al mes», «300 € antes del 15 jul 2027, cada año»). */
export function useResumenObjetivo() {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  return useCallback(
    (
      objetivo: Objetivo | null | 'otro',
      month: string = monthUtils.currentMonth(),
    ): string => {
      if (objetivo === null) {
        return t('No target');
      }
      if (objetivo === 'otro') {
        return t('Advanced automation');
      }
      const importe = format(objetivo.importe, 'financial');
      switch (objetivo.tipo) {
        case 'mensual': {
          const base =
            objetivo.modo === 'rellenar'
              ? t('Refill up to {{amount}} each month', { amount: importe })
              : t('{{amount}} each month', { amount: importe });
          return objetivo.dia
            ? t('{{text}}, by day {{day}}', { text: base, day: objetivo.dia })
            : base;
        }
        case 'semanal':
          return objetivo.modo === 'rellenar'
            ? t('Refill up to {{amount}} each week', { amount: importe })
            : t('{{amount}} each week', { amount: importe });
        case 'anual': {
          // La próxima vuelta, no la fecha de la plantilla si ya pasó.
          const fecha = monthUtils.format(
            proximoVencimiento(objetivo, `${month}-01`),
            'd MMM yyyy',
            locale,
          );
          const cada =
            objetivo.cadaMeses === 12
              ? t('every year')
              : objetivo.cadaMeses === 24
                ? t('every 2 years')
                : t('every {{count}} months', { count: objetivo.cadaMeses });
          return t('{{amount}} by {{date}}, {{repeat}}', {
            amount: importe,
            date: fecha,
            repeat: cada,
          });
        }
        case 'una-vez':
          return t('{{amount}} by {{date}}', {
            amount: importe,
            date: monthUtils.format(objetivo.fecha, 'd MMM yyyy', locale),
          });
        default:
          return '';
      }
    },
    [format, locale, t],
  );
}

/**
 * Guarda un objetivo en la nota de la categoría (la nota manda): sustituye las
 * líneas `#template`/`#goal`/`#objetivo`, vuelve al modo notas si estaba en la
 * UI de automatizaciones, parsea la nota a `goal_def` y recalcula el objetivo
 * del mes. Lo usan la ficha de categoría y los consejos.
 *
 * Si la meta deja de tener fecha (se quita o pasa a mensual) y la categoría
 * tenía una travesía navegando, la travesía se archiva sola (con su carta).
 * `mensaje` vacío: sin aviso (quien llama pone el suyo).
 */
export function useGuardarObjetivo(categoryId: string, month: string) {
  const dispatch = useDispatch();
  const store = useStore();
  const { cerrarTravesia } = useAccionesRecuerdo();
  const { data: category } = useCategory(categoryId);
  const nota = useNotes(categoryId);
  const source =
    category != null && category.template_settings?.source === 'ui'
      ? 'ui'
      : 'notes';
  const [guardando, setGuardando] = useState(false);

  const guardar = useCallback(
    async (
      lineas: string[],
      mensaje: string,
      /**
       * B4 · H49: botón del aviso, calculado ya con el objetivo guardado
       * («Asignar X € ahora»). `null`: aviso sin botón.
       */
      boton?: () => Promise<Notification['button'] | null>,
    ): Promise<boolean> => {
      if (guardando) {
        return false;
      }
      setGuardando(true);
      try {
        await send('notes-save', {
          id: categoryId,
          note: notaConObjetivo(nota, lineas),
        });
        if (source === 'ui') {
          await send('budget/set-category-automations', {
            categoriesWithTemplates: [{ id: categoryId, templates: [] }],
            source: 'notes',
          });
        }
        await send('budget/store-note-templates', [categoryId]);
        if (!lineasConFecha(lineas)) {
          const vivas = navegandoDe(
            recuerdosDesdePrefs(store.getState().prefs.synced),
            categoryId,
          );
          for (const r of vivas) {
            await cerrarTravesia(r.base.rid, { tipo: 'archivar' });
          }
        }
        await send('budget/refresh-goals', { month });
        if (month !== monthUtils.currentMonth()) {
          await send('budget/refresh-goals', {
            month: monthUtils.currentMonth(),
          });
        }
        const accion = boton ? await boton() : null;
        if (mensaje) {
          dispatch(
            addNotification({
              notification: {
                type: 'message',
                message: mensaje,
                timeout: accion ? 10000 : 4000,
                ...(accion && { button: accion }),
              },
            }),
          );
        }
        return true;
      } finally {
        setGuardando(false);
      }
    },
    [
      categoryId,
      cerrarTravesia,
      dispatch,
      guardando,
      month,
      nota,
      source,
      store,
    ],
  );

  return { guardar, guardando };
}

export function Pestanas({
  tipo,
  onChange,
}: {
  tipo: TipoObjetivo;
  onChange: (tipo: TipoObjetivo) => void;
}) {
  const { t } = useTranslation();
  const opciones: [TipoObjetivo, string][] = [
    ['mensual', t('Every month')],
    ['semanal', t('Week')],
    ['anual', t('Year')],
    ['una-vez', t('Once')],
  ];
  return (
    <View
      role="group"
      style={{
        flexDirection: 'row',
        gap: 4,
        padding: 4,
        borderRadius: radio.boton,
        overflow: 'hidden',
        backgroundColor: color.surface2,
      }}
    >
      {opciones.map(([valor, texto]) => {
        const activa = valor === tipo;
        return (
          <Button
            key={valor}
            variant="bare"
            aria-pressed={activa}
            onPress={() => onChange(valor)}
            data-testid={`objetivo-tipo-${valor}`}
            style={{
              flex: 1,
              height: ALTO_BOTON,
              minHeight: ALTO_BOTON,
              borderRadius: 11,
              padding: 0,
              fontSize: 13,
              whiteSpace: 'nowrap',
              fontWeight: 800,
              color: activa ? color.accent : color.fg3,
              backgroundColor: activa ? color.accentSoft : 'transparent',
              transition: `background-color ${movimiento.pildora}ms, color ${movimiento.pildora}ms`,
            }}
          >
            {texto}
          </Button>
        );
      })}
    </View>
  );
}
